import type { DiligenceArtifact, Message } from '../types';

type Metadata = Record<string, unknown>;
interface MetadataReader {
  readArtifacts(ids: string[]): Promise<Map<string, Metadata>>;
  readSource(path: string): Promise<Metadata | null>;
}
const safeId = (value: unknown): value is string => typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/u.test(value);
const label = (value: unknown) => typeof value === 'string' ? value.trim() : '';

/** Read-only compatibility for messages written before template names were projected. */
export function createArtifactTemplateMetadataResolver(uid: string, chatId: string, reader: MetadataReader) {
  // The caller creates one resolver per authenticated chat listener, never a global cache.
  const artifacts = new Map<string, Promise<Metadata | null>>();
  const sources = new Map<string, Promise<Metadata | null>>();
  const readSource = (path: string) => {
    if (!sources.has(path)) sources.set(path, Promise.resolve().then(() => reader.readSource(path)).catch(error => {
      sources.delete(path);
      throw error;
    }));
    return sources.get(path)!;
  };
  async function resolve(artifact: DiligenceArtifact) {
    const data = await artifacts.get(artifact.id);
    if (!data || data.uid !== uid || data.chatId !== chatId || data.status !== 'ready') return undefined;
    const templateId = data.templateId;
    const versionId = data.templateVersionId || data.versionId;
    if ((artifact.templateId && artifact.templateId !== templateId)
      || (artifact.templateVersionId && artifact.templateVersionId !== versionId)
      || (artifact.templateSource && data.templateSource && artifact.templateSource !== data.templateSource)) return undefined;
    const persisted = label(data.templateName);
    if (persisted) return persisted;
    if (!safeId(templateId) || !safeId(versionId)) return undefined;
    const source = data.templateSource || artifact.templateSource;
    if (source !== undefined && source !== 'library' && source !== 'attachment') return undefined;
    const candidates = await Promise.all([
      source !== 'attachment' ? readSource(`users/${uid}/diligenceTemplates/${templateId}/versions/${versionId}`)
        .then(version => version?.uid === uid ? { name: label(version.originalFileName) } : null) : null,
      source !== 'library' ? readSource(`chats/${chatId}/attachments/${templateId}`)
        .then(attachment => attachment?.uid === uid && attachment.chatId === chatId
          && String(attachment.generation) === versionId ? { name: label(attachment.originalFileName) } : null) : null
    ]);
    const matches = candidates.filter(candidate => candidate !== null);
    // Missing source must not silently choose between colliding library/attachment IDs.
    return matches.length === 1 ? matches[0].name || undefined : undefined;
  }
  return async (messages: Message[]): Promise<Message[]> => {
    const missing = messages.flatMap(message => [...(message.artifacts || []), ...(message.artifact ? [message.artifact] : [])])
      .filter(artifact => !label(artifact.templateName) && safeId(artifact.id));
    const uncached = [...new Set(missing.map(artifact => artifact.id))].filter(id => !artifacts.has(id));
    for (let start = 0; start < uncached.length; start += 10) {
      const ids = uncached.slice(start, start + 10);
      const batch = Promise.resolve().then(() => reader.readArtifacts(ids)).catch(() => {
        for (const id of ids) artifacts.delete(id);
        return new Map<string, Metadata>();
      });
      for (const id of ids) artifacts.set(id, batch.then(records => records.get(id) || null));
    }
    if (!missing.length) return messages;
    const resolved = await Promise.all(missing.map(async artifact => [artifact, await resolve(artifact).catch(() => undefined)] as const));
    const names = new Map(resolved);
    const enrich = (artifact: DiligenceArtifact) => !label(artifact.templateName) && names.get(artifact)
      ? { ...artifact, templateName: names.get(artifact) } : artifact;
    return messages.map(message => ({ ...message,
      ...(message.artifact ? { artifact: enrich(message.artifact) } : {}),
      ...(message.artifacts ? { artifacts: message.artifacts.map(enrich) } : {})
    }));
  };
}
